import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SubHeader from "components/common/SubHeader";
import CommonModal from "components/common/CommonModal";
import { postPaymentAllCartItems, getDeliveryFee } from "api/cart";
import { useSnackbar } from "contexts/SnackbarContext";
import useUserStore from "@store/useUserStore";
import "assets/scss/pages/cartCheckout.scss";

const CartCheckout = () => {
    const { showError, showSuccess } = useSnackbar();
    const navigate = useNavigate();
    const location = useLocation();
    const userInfo = useUserStore((state) => state.userInfo);

    // Cart 페이지에서 전달받은 데이터
    const { selectedItems = [], cartItemIds = [] } = location.state || {};

    const [itemList, setItemList] = useState(selectedItems);
    const [buyerInfo, setBuyerInfo] = useState({
        name: "",
        tel: "",
        email: "",
    });
    const [shippingInfo, setShippingInfo] = useState({
        zipCode: "",
        address: "",
        detailAddress: "",
        deliveryMessage: "",
    });

    const [deliveryFee, setDeliveryFee] = useState({});
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [errors, setErrors] = useState({
        name: "",
        tel: "",
        email: "",

        zipCode: "",
        address: "",
        detailAddress: "",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isItemsExpanded, setIsItemsExpanded] = useState(false);
    const [isLoadingDeliveryFee, setIsLoadingDeliveryFee] = useState(false);

    // 초기 로딩 완료 여부를 추적
    const isInitialLoadComplete = useRef(false);

    // 배송비 조회 함수 (재사용 가능하도록 분리)
    const fetchDeliveryFee = useCallback(
        async (zipCode, address, detailAddress) => {
            // zipCode가 없으면 배송비 조회 불가
            if (!zipCode || !address) {
                return;
            }

            setIsLoadingDeliveryFee(true);

            try {
                const res = await getDeliveryFee({
                    cart_item_ids: cartItemIds,
                    postal_code: zipCode,
                    address: address + " " + detailAddress,
                });

                setDeliveryFee(res.summary);

                // 각 아이템에 배송비 정보 추가 (함수형 업데이트로 itemList 의존 제거)
                setItemList((prev) =>
                    prev.map((item, idx) => ({
                        ...item,
                        shipping_fee: res.items[idx].total_shipping_fee,
                    }))
                );
            } catch (err) {
                console.error("배송비 조회 실패:", err);
                showError("배송비 조회에 실패했습니다.");
            } finally {
                setIsLoadingDeliveryFee(false);
            }
        },
        [cartItemIds, showError]
    );

    // 사용자 정보로 초기화
    useEffect(() => {
        if (!itemList || itemList.length === 0) {
            showError("주문할 상품이 없습니다.");
            navigate("/cart");
            return;
        }

        if (userInfo && !isInitialLoadComplete.current) {
            setBuyerInfo({
                name: userInfo.name || "",
                tel: userInfo.hp || "",
                email: userInfo.user_email || "",
            });

            // 대표 주소가 있으면 자동 선택, 없으면 첫 주소로 폴백(PaymentInfo와 동작 통일)
            const autoSelectedAddress =
                userInfo.addresses?.find((address) => address.is_primary) ??
                userInfo.addresses?.[0];
            if (autoSelectedAddress) {
                setSelectedAddress(autoSelectedAddress);
                const newShippingInfo = {
                    zipCode: autoSelectedAddress.zipcode || "",
                    address: autoSelectedAddress.road_address || "",
                    detailAddress: autoSelectedAddress.detail_address || "",
                    deliveryMessage: "",
                };
                setShippingInfo(newShippingInfo);

                // 1. 저장된 주소가 있다면 배송비 조회
                fetchDeliveryFee(
                    newShippingInfo.zipCode,
                    newShippingInfo.address,
                    newShippingInfo.detailAddress
                );
            }

            // 초기 로딩 완료 표시
            isInitialLoadComplete.current = true;
        }
    }, [userInfo, itemList, navigate, showError, fetchDeliveryFee]);

    // 주석: 기존 useEffect (빈 의존성 배열로 인해 제대로 동작하지 않았음)
    // useEffect(() => {
    //     if (shippingInfo.zipCode) {
    //         getDeliveryFee({
    //             cart_item_ids: cartItemIds,
    //             postal_code: shippingInfo.zipCode,
    //             address:
    //                 shippingInfo.address + " " + shippingInfo.detailAddress,
    //         }).then((res) => {
    //             setDeliveryFee(res.summary);
    //             const newItemList = itemList.map((item, idx) => ({
    //                 ...item,
    //                 shipping_fee: res.items[idx].total_shipping_fee,
    //             }));
    //             setItemList(newItemList);
    //         });
    //     }
    // }, []);

    // 입력 변경 핸들러
    const handleInputChange = (field, value) => {
        setBuyerInfo((prev) => ({
            ...prev,
            [field]: value,
        }));

        // 입력 시 에러 메시지 초기화
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }
    };

    // 배송지 정보 입력 변경 핸들러
    const handleShippingInputChange = (field, value) => {
        setShippingInfo((prev) => ({
            ...prev,
            [field]: value,
        }));

        // 입력 시 에러 메시지 초기화
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }
    };

    // 주소 검색 버튼 핸들러 (저장된 주소 모달 열기)
    const handleAddressSearch = () => {
        setIsAddressModalOpen(true);
    };

    // 주소 선택 핸들러
    const handleSelectAddress = (address) => {
        setSelectedAddress(address);

        const newShippingInfo = {
            zipCode: address.zipcode || "",
            address: address.road_address || "",
            detailAddress: address.detail_address || "",
            deliveryMessage: shippingInfo.deliveryMessage, // 배송 메시지는 유지
        };

        setShippingInfo(newShippingInfo);

        // 주소 관련 에러 메시지 초기화
        setErrors((prev) => ({
            ...prev,
            zipCode: "",
            address: "",
            detailAddress: "",
        }));

        setIsAddressModalOpen(false);

        // 2. 주소를 변경하면 변경된 주소를 기준으로 배송비를 다시 조회
        fetchDeliveryFee(
            newShippingInfo.zipCode,
            newShippingInfo.address,
            newShippingInfo.detailAddress
        );
    };

    // 유효성 검사
    const validateForm = () => {
        const newErrors = {
            name: "",
            tel: "",
            email: "",
            zipCode: "",
            address: "",
            detailAddress: "",
        };

        let isValid = true;

        // 이름 검사
        if (!buyerInfo.name || buyerInfo.name.trim() === "") {
            newErrors.name = "이름을 입력해주세요.";
            isValid = false;
        }

        // 전화번호 검사
        const telPattern = /^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/;
        if (!buyerInfo.tel || buyerInfo.tel.trim() === "") {
            newErrors.tel = "전화번호를 입력해주세요.";
            isValid = false;
        } else if (!telPattern.test(buyerInfo.tel.replace(/-/g, ""))) {
            newErrors.tel = "올바른 전화번호 형식이 아닙니다.";
            isValid = false;
        }

        // 이메일 검사
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!buyerInfo.email || buyerInfo.email.trim() === "") {
            newErrors.email = "이메일을 입력해주세요.";
            isValid = false;
        } else if (!emailPattern.test(buyerInfo.email)) {
            newErrors.email = "올바른 이메일 형식이 아닙니다.";
            isValid = false;
        }

        if (!shippingInfo.zipCode || shippingInfo.zipCode.trim() === "") {
            newErrors.zipCode = "우편번호를 입력해주세요.";
            isValid = false;
        }

        if (!shippingInfo.address || shippingInfo.address.trim() === "") {
            newErrors.address = "주소를 입력해주세요.";
            isValid = false;
        }

        if (
            !shippingInfo.detailAddress ||
            shippingInfo.detailAddress.trim() === ""
        ) {
            newErrors.detailAddress = "상세주소를 입력해주세요.";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    // 결제하기 핸들러
    const handlePayment = async () => {
        // 중복 실행 방지
        if (isSubmitting) return;

        // 유효성 검사
        if (!validateForm()) {
            showError("입력 정보를 확인해주세요.");
            return;
        }

        setIsSubmitting(true);

        try {
            const res = await postPaymentAllCartItems({
                buyer_name: buyerInfo.name,
                buyer_tel: buyerInfo.tel,
                buyer_email: buyerInfo.email,
                cart_item_ids: cartItemIds,
                shipping_address:
                    shippingInfo.address + " " + shippingInfo.detailAddress,
                shipping_zip_code: shippingInfo.zipCode,
                // BE CartCheckoutView 가 최상위 shipping_memo 를 읽어 shipping_info 에 저장한다.
                shipping_memo: shippingInfo.deliveryMessage,
            });
            if (
                res?.message ===
                "결제 준비가 완료되었습니다. 5분 이내에 결제를 완료해주세요."
            ) {
                const paymentInfo = res.data.payment_params;
                if (!window.innopay) {
                    alert("결제 모듈이 아직 준비되지 않았습니다.");
                    return;
                }
                window.innopay.goPay(paymentInfo);
            } else {
                showError(res?.message || "결제에 실패했습니다. 다시 시도해주세요.");
            }
        } catch (err) {
            console.error("결제 실패:", err);
            showError(
                err?.response?.data?.error ||
                    "결제에 실패했습니다. 다시 시도해주세요."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    // 아코디언 토글 핸들러
    const handleToggleItems = () => {
        setIsItemsExpanded((prev) => !prev);
    };

    // 가격 포맷팅
    const formatPrice = (price) => {
        return Number(price).toLocaleString("ko-KR");
    };

    // 총 상품 금액 계산
    const totalItemPrice = useMemo(() => {
        return itemList.reduce((total, item) => {
            const itemPrice = Number(item.item_price);
            const quantity = item.quantity;
            return total + itemPrice * quantity;
        }, 0);
    }, [itemList]);

    return (
        <div className="cart-checkout-page">
            <SubHeader title="주문/결제" />

            <div className="cart-checkout-page__container">
                {/* 주문자 정보 섹션 */}
                <section className="cart-checkout-page__section">
                    <h2 className="cart-checkout-page__section-title">
                        주문자 정보
                    </h2>
                    <div className="cart-checkout-page__form">
                        <div className="cart-checkout-page__form-group">
                            <label
                                htmlFor="buyer-name"
                                className="cart-checkout-page__label"
                            >
                                이름{" "}
                                <span className="cart-checkout-page__required">
                                    *
                                </span>
                            </label>
                            <input
                                type="text"
                                id="buyer-name"
                                className={`cart-checkout-page__input ${
                                    errors.name
                                        ? "cart-checkout-page__input--error"
                                        : ""
                                }`}
                                value={buyerInfo.name}
                                onChange={(e) =>
                                    handleInputChange("name", e.target.value)
                                }
                                placeholder="이름을 입력해주세요"
                                aria-label="주문자 이름"
                                aria-required="true"
                                aria-invalid={errors.name ? "true" : "false"}
                            />
                            {errors.name && (
                                <span className="cart-checkout-page__error-message">
                                    {errors.name}
                                </span>
                            )}
                        </div>

                        <div className="cart-checkout-page__form-group">
                            <label
                                htmlFor="buyer-tel"
                                className="cart-checkout-page__label"
                            >
                                연락처{" "}
                                <span className="cart-checkout-page__required">
                                    *
                                </span>
                            </label>
                            <input
                                type="tel"
                                id="buyer-tel"
                                className={`cart-checkout-page__input ${
                                    errors.tel
                                        ? "cart-checkout-page__input--error"
                                        : ""
                                }`}
                                value={buyerInfo.tel}
                                onChange={(e) =>
                                    handleInputChange("tel", e.target.value)
                                }
                                placeholder="010-1234-5678"
                                aria-label="주문자 연락처"
                                aria-required="true"
                                aria-invalid={errors.tel ? "true" : "false"}
                            />
                            {errors.tel && (
                                <span className="cart-checkout-page__error-message">
                                    {errors.tel}
                                </span>
                            )}
                        </div>

                        <div className="cart-checkout-page__form-group">
                            <label
                                htmlFor="buyer-email"
                                className="cart-checkout-page__label"
                            >
                                이메일{" "}
                                <span className="cart-checkout-page__required">
                                    *
                                </span>
                            </label>
                            <input
                                type="email"
                                id="buyer-email"
                                className={`cart-checkout-page__input ${
                                    errors.email
                                        ? "cart-checkout-page__input--error"
                                        : ""
                                }`}
                                value={buyerInfo.email}
                                onChange={(e) =>
                                    handleInputChange("email", e.target.value)
                                }
                                placeholder="example@email.com"
                                aria-label="주문자 이메일"
                                aria-required="true"
                                aria-invalid={errors.email ? "true" : "false"}
                            />
                            {errors.email && (
                                <span className="cart-checkout-page__error-message">
                                    {errors.email}
                                </span>
                            )}
                        </div>
                    </div>
                </section>

                {/* 배송지 정보 섹션 */}
                <section className="cart-checkout-page__section">
                    <h2 className="cart-checkout-page__section-title">
                        배송지 정보
                    </h2>

                    <div className="cart-checkout-page__form">
                        {/* 우편번호 */}
                        <div className="cart-checkout-page__form-group">
                            <label
                                htmlFor="zip-code"
                                className="cart-checkout-page__label"
                            >
                                우편번호{" "}
                                <span className="cart-checkout-page__required">
                                    *
                                </span>
                            </label>
                            <div className="cart-checkout-page__address-search">
                                <input
                                    type="text"
                                    id="zip-code"
                                    className={`cart-checkout-page__input ${
                                        errors.zipCode
                                            ? "cart-checkout-page__input--error"
                                            : ""
                                    }`}
                                    value={shippingInfo.zipCode}
                                    onChange={(e) =>
                                        handleShippingInputChange(
                                            "zipCode",
                                            e.target.value
                                        )
                                    }
                                    placeholder="우편번호"
                                    aria-label="우편번호"
                                    aria-required="true"
                                    aria-invalid={
                                        errors.zipCode ? "true" : "false"
                                    }
                                    readOnly
                                />
                                <button
                                    type="button"
                                    className="cart-checkout-page__address-search-btn"
                                    onClick={handleAddressSearch}
                                >
                                    주소 검색
                                </button>
                            </div>
                            {errors.zipCode && (
                                <span className="cart-checkout-page__error-message">
                                    {errors.zipCode}
                                </span>
                            )}
                        </div>

                        {/* 기본 주소 */}
                        <div className="cart-checkout-page__form-group">
                            <label
                                htmlFor="address"
                                className="cart-checkout-page__label"
                            >
                                주소{" "}
                                <span className="cart-checkout-page__required">
                                    *
                                </span>
                            </label>
                            <input
                                type="text"
                                id="address"
                                className={`cart-checkout-page__input ${
                                    errors.address
                                        ? "cart-checkout-page__input--error"
                                        : ""
                                }`}
                                value={shippingInfo.address}
                                onChange={(e) =>
                                    handleShippingInputChange(
                                        "address",
                                        e.target.value
                                    )
                                }
                                placeholder="기본 주소"
                                aria-label="기본 주소"
                                aria-required="true"
                                aria-invalid={errors.address ? "true" : "false"}
                                readOnly
                            />
                            {errors.address && (
                                <span className="cart-checkout-page__error-message">
                                    {errors.address}
                                </span>
                            )}
                        </div>

                        {/* 상세 주소 */}
                        <div className="cart-checkout-page__form-group">
                            <label
                                htmlFor="detail-address"
                                className="cart-checkout-page__label"
                            >
                                상세주소{" "}
                                <span className="cart-checkout-page__required">
                                    *
                                </span>
                            </label>
                            <input
                                type="text"
                                id="detail-address"
                                className={`cart-checkout-page__input ${
                                    errors.detailAddress
                                        ? "cart-checkout-page__input--error"
                                        : ""
                                }`}
                                value={shippingInfo.detailAddress}
                                onChange={(e) =>
                                    handleShippingInputChange(
                                        "detailAddress",
                                        e.target.value
                                    )
                                }
                                placeholder="상세 주소를 입력해주세요"
                                aria-label="상세 주소"
                                aria-required="true"
                                aria-invalid={
                                    errors.detailAddress ? "true" : "false"
                                }
                            />
                            {errors.detailAddress && (
                                <span className="cart-checkout-page__error-message">
                                    {errors.detailAddress}
                                </span>
                            )}
                        </div>

                        {/* 배송 메시지 (선택) */}
                        <div className="cart-checkout-page__form-group">
                            <label
                                htmlFor="delivery-message"
                                className="cart-checkout-page__label"
                            >
                                배송 메시지
                            </label>
                            <input
                                type="text"
                                id="delivery-message"
                                className="cart-checkout-page__input"
                                value={shippingInfo.deliveryMessage}
                                onChange={(e) =>
                                    handleShippingInputChange(
                                        "deliveryMessage",
                                        e.target.value
                                    )
                                }
                                placeholder="배송 시 요청사항을 입력해주세요 (선택)"
                                aria-label="배송 메시지"
                                maxLength={100}
                            />
                        </div>
                    </div>
                </section>

                {/* 주문 상품 정보 섹션 */}
                <section className="cart-checkout-page__section">
                    <h2 className="cart-checkout-page__section-title">
                        주문 상품 정보 ({itemList.length}개)
                    </h2>
                    <div className="cart-checkout-page__items">
                        {/* 첫 번째 상품은 항상 표시 */}
                        {itemList.length > 0 && (
                            <div
                                key={itemList[0].id}
                                className="cart-checkout-page__item"
                            >
                                <div className="cart-checkout-page__item-image">
                                    {/* 이미지 영역 */}
                                    <img
                                        src={itemList[0].images.image}
                                        alt={itemList[0].name}
                                    />
                                </div>
                                <div className="cart-checkout-page__item-info">
                                    <h3 className="cart-checkout-page__item-name">
                                        {itemList[0].name}
                                    </h3>
                                    {itemList[0].selected_options &&
                                        itemList[0].selected_options.length >
                                            0 && (
                                            <p className="cart-checkout-page__item-option">
                                                옵션:{" "}
                                                {itemList[0].selected_options
                                                    .map(
                                                        (option) =>
                                                            option.option_value
                                                    )
                                                    .join("/")}
                                            </p>
                                        )}
                                    <div className="cart-checkout-page__item-price-row">
                                        <span className="cart-checkout-page__item-unit-price">
                                            {formatPrice(
                                                itemList[0].item_price
                                            )}
                                            원
                                        </span>
                                        <span className="cart-checkout-page__item-quantity">
                                            수량: {itemList[0].quantity}개
                                        </span>
                                    </div>

                                    <div className="cart-checkout-page__item-total">
                                        <p>
                                            <span className="cart-checkout-page__item-total-label">
                                                배송비
                                            </span>
                                            <span className="cart-checkout-page__item-total-price">
                                                {/* {formatPrice(
                                                    Number(
                                                        itemList[0]
                                                            .shipping_fee
                                                    )
                                                            
                                                )} */}
                                                {formatPrice(
                                                    itemList[0].shipping_fee
                                                )}
                                                원
                                            </span>
                                        </p>
                                        <p>
                                            <span className="cart-checkout-page__item-total-label">
                                                상품 금액
                                            </span>
                                            <span className="cart-checkout-page__item-total-price">
                                                {formatPrice(
                                                    Number(
                                                        itemList[0].item_price
                                                    ) * itemList[0].quantity
                                                )}
                                                원
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* 나머지 상품들 - 아코디언으로 접기/펼치기 */}
                        {isItemsExpanded && itemList.length > 1 && (
                            <>
                                {itemList.slice(1).map((item) => (
                                    <div
                                        key={item.id}
                                        className="cart-checkout-page__item cart-checkout-page__item--accordion"
                                    >
                                        <div className="cart-checkout-page__item-image">
                                            {/* 이미지 영역 */}
                                            <img
                                                src={item.images.image}
                                                alt={item.name}
                                            />
                                        </div>
                                        <div className="cart-checkout-page__item-info">
                                            <h3 className="cart-checkout-page__item-name">
                                                {item.name}
                                            </h3>
                                            {item.selected_options &&
                                                item.selected_options.length >
                                                    0 && (
                                                    <p className="cart-checkout-page__item-option">
                                                        옵션:{" "}
                                                        {item.selected_options
                                                            .map(
                                                                (option) =>
                                                                    option.option_value
                                                            )
                                                            .join("/")}
                                                    </p>
                                                )}
                                            <div className="cart-checkout-page__item-price-row">
                                                <span className="cart-checkout-page__item-unit-price">
                                                    {formatPrice(
                                                        item.item_price
                                                    )}
                                                    원
                                                </span>
                                                <span className="cart-checkout-page__item-quantity">
                                                    수량: {item.quantity}개
                                                </span>
                                            </div>
                                            <div className="cart-checkout-page__item-total">
                                                <p>
                                                    <span className="cart-checkout-page__item-total-label">
                                                        배송비
                                                    </span>
                                                    <span className="cart-checkout-page__item-total-price">
                                                        {formatPrice(
                                                            item.shipping_fee
                                                        )}
                                                        원
                                                    </span>
                                                </p>
                                                <p>
                                                    <span className="cart-checkout-page__item-total-label">
                                                        상품 금액
                                                    </span>
                                                    <span className="cart-checkout-page__item-total-price">
                                                        {formatPrice(
                                                            Number(
                                                                item.item_price
                                                            ) * item.quantity
                                                        )}
                                                        원
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>

                    {/* 모두보기 버튼 - 상품이 2개 이상일 때만 표시 */}
                    {itemList.length > 1 && (
                        <button
                            type="button"
                            className="cart-checkout-page__toggle-btn"
                            onClick={handleToggleItems}
                            aria-expanded={isItemsExpanded}
                            aria-label={
                                isItemsExpanded
                                    ? "상품 목록 접기"
                                    : "상품 목록 펼치기"
                            }
                        >
                            <span className="cart-checkout-page__toggle-btn-text">
                                {isItemsExpanded
                                    ? "접기"
                                    : `모두보기 (${
                                          itemList.length - 1
                                      }개 더보기)`}
                            </span>
                            <svg
                                className={`cart-checkout-page__toggle-btn-icon ${
                                    isItemsExpanded
                                        ? "cart-checkout-page__toggle-btn-icon--expanded"
                                        : ""
                                }`}
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M4 6L8 10L12 6"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    )}
                </section>

                {/* 결제 금액 섹션 */}
                <section className="cart-checkout-page__section">
                    <h2 className="cart-checkout-page__section-title">
                        결제 금액
                    </h2>
                    <div className="cart-checkout-page__payment-summary">
                        <div className="cart-checkout-page__payment-row">
                            <span className="cart-checkout-page__payment-label">
                                총 상품 금액
                            </span>
                            <span className="cart-checkout-page__payment-value">
                                {formatPrice(totalItemPrice)}원
                            </span>
                        </div>
                        <div className="cart-checkout-page__payment-row">
                            <span className="cart-checkout-page__payment-label">
                                배송비
                            </span>
                            <span className="cart-checkout-page__payment-value">
                                {deliveryFee === 0
                                    ? "무료"
                                    : `${formatPrice(
                                          deliveryFee.total_base_shipping_fee +
                                              deliveryFee.total_regional_surcharge
                                      )}원`}
                            </span>
                        </div>
                        <div className="cart-checkout-page__payment-divider" />
                        <div className="cart-checkout-page__payment-row cart-checkout-page__payment-row--total">
                            <span className="cart-checkout-page__payment-total-label">
                                최종 결제 금액
                            </span>
                            <span className="cart-checkout-page__payment-total-value">
                                {formatPrice(deliveryFee.final_amount)}원
                            </span>
                        </div>
                        <button
                            type="button"
                            className="cart-checkout-page__payment-btn"
                            onClick={handlePayment}
                            disabled={isSubmitting || itemList.length === 0}
                            aria-label="결제하기"
                        >
                            {isSubmitting ? "처리 중..." : "결제하기"}
                        </button>
                    </div>
                </section>
            </div>

            {/* 주소 선택 모달 */}
            <CommonModal
                isOpen={isAddressModalOpen}
                onRequestClose={() => setIsAddressModalOpen(false)}
                title="배송지 선택"
                className="cart-checkout-page__address-modal"
            >
                <div className="cart-checkout-page__address-list">
                    {userInfo?.addresses?.map((address) => (
                        <div
                            key={address.id}
                            className={`cart-checkout-page__address-card ${
                                selectedAddress?.id === address.id
                                    ? "cart-checkout-page__address-card--selected"
                                    : ""
                            }`}
                            onClick={() => handleSelectAddress(address)}
                        >
                            <div className="cart-checkout-page__address-card-header">
                                <span className="cart-checkout-page__address-label">
                                    {address.label || "배송지"}
                                </span>
                                {address.is_primary && (
                                    <span className="cart-checkout-page__address-badge">
                                        대표
                                    </span>
                                )}
                            </div>
                            <div className="cart-checkout-page__address-info">
                                [{address.zipcode}] {address.road_address}{" "}
                                {address.detail_address || ""}
                            </div>
                        </div>
                    ))}
                </div>
                {(!userInfo?.addresses || userInfo.addresses.length === 0) && (
                    <div className="cart-checkout-page__no-address">
                        <p>저장된 배송지가 없습니다.</p>
                        <button
                            type="button"
                            className="cart-checkout-page__add-address-btn"
                            onClick={() => navigate("/mypage/address/edit")}
                        >
                            배송지 추가하기
                        </button>
                    </div>
                )}
            </CommonModal>
        </div>
    );
};

export default CartCheckout;
